import { useEffect, useState } from 'react';

import { Data as Basket } from '../../../../weborder/BasketControl';
import { getCrossSellMenuItemIds } from '../../../api/menu/menu.service';
import {
  CrossSellMenuItems,
  Menu,
  MenuItem,
} from '../../../api/menu/menu.types';
import { flagService } from '../../../services/flagService';
import { Async, AxiosError, Status } from '../../../types/data.types';

interface UseCrossSellMenuItems {
  data: MenuItem[];
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
}

export const useCrossSellMenuItems = (
  basketItems: Basket['items'],
  menu: Menu,
  orderTotal: number,
  limit?: number,
): UseCrossSellMenuItems => {
  const [crossSellMenuItems, setCrossSellMenuItems] = useState<
    Async<MenuItem[]>
  >({
    status: Status.LOADING,
  });
  const isError = crossSellMenuItems.status === Status.ERROR;
  const isLoading = crossSellMenuItems.status === Status.LOADING;
  const isSuccess = crossSellMenuItems.status === Status.SUCCESS;
  const data = isSuccess ? crossSellMenuItems.data : [];

  const isCrossSellEnabled = flagService.isFlagOn('crossSell');

  const getCrossSellMenuItems = (
    menuItemIds: CrossSellMenuItems['MenuItemIds'],
  ): MenuItem[] => {
    const menuItems: MenuItem[] = [];
    menu.MenuSections.forEach((section) => {
      section.MenuItems.forEach((item) => {
        const isMenuItemFound = menuItemIds.some(
          (id) => id === item.MenuItemId,
        );
        if (isMenuItemFound) {
          menuItems.push(item);
        }
      });
    });

    return menuItems;
  };

  useEffect(() => {
    const hasMenuData =
      !!menu?.MenuId && basketItems.length > 0 && orderTotal >= 0;
    if (isCrossSellEnabled && !isSuccess && hasMenuData) {
      if (!isLoading) {
        setCrossSellMenuItems({ status: Status.LOADING });
      }

      const menuItemIds = basketItems.map(
        (basketItem) => basketItem.Item.MenuItemId,
      );

      const queryParams = {
        limit: limit || 6,
        menuId: menu.MenuId,
        menuItemId: menuItemIds,
        totalValue: orderTotal,
      };
      getCrossSellMenuItemIds(queryParams)
        .then((response) => getCrossSellMenuItems(response?.MenuItemIds || []))
        .then((menuItems) =>
          setCrossSellMenuItems({ status: Status.SUCCESS, data: menuItems }),
        )
        .catch((error: AxiosError) =>
          setCrossSellMenuItems({ status: Status.ERROR, error }),
        );
    }
  }, [basketItems.length, limit, menu?.MenuId, orderTotal]);

  return {
    data,
    isError,
    isLoading,
    isSuccess,
  };
};
